/**
 * metadata — the client to the Night Light proxy (see contracts/proxy-api.md).
 *
 * All calls go through the proxy; we never hit archive.org directly from the
 * browser because the Metadata API does not emit CORS headers.
 *
 * The client is mediatype-aware: when the identifier resolves to a
 * *collection* rather than an item (FR-011), the promise rejects with a
 * tagged error so the UI can surface a friendly message rather than
 * attempting to play it.
 */

import type { MetadataResponse } from "../../types.ts";

const ID_RE = /^[a-zA-Z0-9._-]+$/;

const PROXY_BASE: string =
  (import.meta.env?.VITE_PROXY_BASE as string | undefined) ??
  "http://localhost:8787";

export class MetadataError extends Error {
  constructor(
    public kind:
      | "bad-id"
      | "not-found"
      | "unreachable"
      | "collection"
      | "parse"
      | "network",
    message: string,
  ) {
    super(message);
    this.name = "MetadataError";
  }
}

export async function fetchMetadata(identifier: string): Promise<MetadataResponse> {
  if (!ID_RE.test(identifier)) {
    throw new MetadataError("bad-id", `Invalid archive.org identifier: ${identifier}`);
  }

  let res: Response;
  try {
    res = await fetch(`${PROXY_BASE}/${encodeURIComponent(identifier)}`);
  } catch (err) {
    throw new MetadataError(
      "network",
      `Network error reaching the Night Light proxy: ${(err as Error).message}`,
    );
  }

  if (res.status === 400) throw new MetadataError("bad-id", "Proxy rejected the identifier");
  if (res.status === 404) throw new MetadataError("not-found", "Archive.org has no item with this ID");
  if (res.status === 502) throw new MetadataError("unreachable", "Archive.org is unreachable right now");
  if (!res.ok) throw new MetadataError("network", `Proxy returned HTTP ${res.status}`);

  let body: MetadataResponse;
  try {
    body = (await res.json()) as MetadataResponse;
  } catch (err) {
    throw new MetadataError("parse", `Proxy returned non-JSON: ${(err as Error).message}`);
  }

  if (body.metadata?.mediatype === "collection") {
    throw new MetadataError(
      "collection",
      `"${body.metadata.identifier}" is a collection, not a single item`,
    );
  }

  return body;
}
